import { Box, Typography, Paper, CircularProgress } from "@mui/material";
import { CloudUpload, Description } from "@mui/icons-material";
import { useCallback, useState } from "react";

export interface CsvDropzoneProps {
  onFileSelected: (file: File, base64Content: string) => void;
  isLoading: boolean;
  disabled?: boolean;
}

export const CsvDropzone = ({ onFileSelected, isLoading, disabled }: CsvDropzoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      if (!file.name.endsWith(".csv")) {
        alert("Veuillez sélectionner un fichier CSV");
        return;
      }
      setSelectedFile(file);

      // Read file as base64
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data:text/csv;base64, prefix if present
        const base64 = result.includes(",") ? result.split(",")[1] : result;
        onFileSelected(file, base64);
      };
      reader.readAsDataURL(file);
    },
    [onFileSelected]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (disabled || isLoading) return;

      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [disabled, isLoading, handleFile]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled && !isLoading) {
        setIsDragging(true);
      }
    },
    [disabled, isLoading]
  );

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  return (
    <Paper
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      sx={{
        p: 4,
        border: "2px dashed",
        borderColor: isDragging ? "primary.main" : "grey.300",
        backgroundColor: isDragging ? "action.hover" : "background.paper",
        borderRadius: 2,
        textAlign: "center",
        cursor: disabled || isLoading ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.2s ease",
      }}
    >
      <input
        type="file"
        accept=".csv"
        onChange={handleInputChange}
        style={{ display: "none" }}
        id="csv-file-input"
        disabled={disabled || isLoading}
      />
      <label htmlFor="csv-file-input" style={{ cursor: disabled || isLoading ? "not-allowed" : "pointer" }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          {isLoading ? (
            <CircularProgress size={48} />
          ) : selectedFile ? (
            <Description sx={{ fontSize: 48, color: "primary.main" }} />
          ) : (
            <CloudUpload sx={{ fontSize: 48, color: isDragging ? "primary.main" : "grey.400" }} />
          )}

          {isLoading ? (
            <Typography variant="body1" color="text.secondary">
              Analyse du fichier en cours...
            </Typography>
          ) : selectedFile ? (
            <>
              <Typography variant="body1" fontWeight={500}>
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(selectedFile.size / 1024).toFixed(1)} Ko
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" fontWeight={500}>
                Glissez-déposez votre fichier CSV ici
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ou cliquez pour sélectionner un fichier
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Format accepté : CSV (séparateur point-virgule)
              </Typography>
            </>
          )}
        </Box>
      </label>
    </Paper>
  );
};
